import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { header } from '../components/header.encry';
import StoreService from '../components/StoreService';
import { createAccount, createPaymentMethodsBulk, getAccountMovements, getAccounts, getPaymentMethods, updatePaymentMethod } from './api/posApi';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const emptyForm = {
  code: '',
  name: '',
  store_ids: [],
  account_id: '',
  active: true,
};

const emptyAccountForm = {
  name: '',
  type: 'cash_register',
  store_id: '',
  balance: 0,
  currency: 'MAD',
  active: true,
};

const getStoreId = (store) => String(store?._id || store?.id || store?.value || '');

const getStoreCode = (store) => String(store?.storeCode || store?.code || store?.value || '');

const getStoreLabel = (store) => (
  store?.name ||
  store?.storeName ||
  store?.label ||
  store?.title ||
  getStoreCode(store) ||
  getStoreId(store)
);

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.response)) return payload.response;
  return [];
};

const normalizeCode = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

const PaymentMethodsView = () => {
  const [methods, setMethods] = useState([]);
  const [stores, setStores] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [accountForm, setAccountForm] = useState(emptyAccountForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const storeNameById = useMemo(() => {
    const map = new Map();
    stores.forEach((store) => {
      const id = getStoreId(store);
      if (id) map.set(id, getStoreLabel(store));
    });
    return map;
  }, [stores]);

  const groupedMethods = useMemo(() => {
    const byCode = new Map();
    methods.forEach((method) => {
      const key = method.code || method._id;
      if (!byCode.has(key)) {
        byCode.set(key, {
          code: method.code,
          name: method.name,
          total: 0,
          active: 0,
          stores: [],
          rows: [],
        });
      }
      const group = byCode.get(key);
      group.total += 1;
      if (method.active) group.active += 1;
      group.rows.push(method);
      group.stores.push(method.store_id || null);
    });
    return Array.from(byCode.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [methods]);

  const accountById = useMemo(() => {
    const map = new Map();
    accounts.forEach((account) => {
      const id = String(account?._id || account?.id || '');
      if (id) map.set(id, account);
    });
    return map;
  }, [accounts]);

  const getAccountLabel = (account) => {
    if (!account) return 'Aucun compte';
    const storeLabel = account.store_id ? (storeNameById.get(account.store_id) || account.store_id) : 'Global';
    return `${account.name || account.account_name} - ${account.type} - ${storeLabel}`;
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [methodsData, accountsData, storesData, movementsData] = await Promise.all([
        getPaymentMethods(),
        getAccounts(),
        StoreService.fetchStores().catch(async () => {
          const response = await axios.get(`${API_URL}/distinctStoreIds`, header());
          return response.data?.response || response.data || [];
        }),
        getAccountMovements({ limit: 20 }).catch(() => []),
      ]);
      setMethods(normalizeArray(methodsData));
      setAccounts(normalizeArray(accountsData));
      setStores(normalizeArray(storesData));
      setMovements(normalizeArray(movementsData));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Impossible de charger les donnees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async () => {
    const code = normalizeCode(formData.code);
    const name = formData.name.trim();
    if (!code || !name) {
      setError('Code et nom sont obligatoires.');
      return;
    }
    if (!formData.store_ids.length) {
      setError('Selectionnez au moins un magasin.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const created = await createPaymentMethodsBulk({
        code,
        name,
        store_ids: formData.store_ids,
        account_id: formData.account_id || null,
        active: formData.active,
      });
      setFormData(emptyForm);
      setMessage(`${normalizeArray(created).length} methode(s) configuree(s).`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Impossible d enregistrer.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!accountForm.name.trim()) {
      setError('Le nom du compte est obligatoire.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await createAccount({
        ...accountForm,
        store_id: accountForm.store_id || null,
        balance: Number(accountForm.balance) || 0,
      });
      setAccountForm(emptyAccountForm);
      setMessage('Compte cree.');
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Impossible de creer le compte.');
    } finally {
      setSaving(false);
    }
  };

  const toggleMethod = async (method, active) => {
    setError('');
    setMessage('');
    try {
      await updatePaymentMethod(method._id, { active });
      setMethods((prev) => prev.map((item) => (item._id === method._id ? { ...item, active } : item)));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Impossible de modifier cette methode.');
    }
  };

  const updateMethodAccount = async (method, accountId) => {
    setError('');
    setMessage('');
    try {
      const updated = await updatePaymentMethod(method._id, { account_id: accountId || null });
      setMethods((prev) => prev.map((item) => (item._id === method._id ? updated : item)));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Impossible de lier ce compte.');
    }
  };

  const selectAllStores = () => {
    setFormData((prev) => ({ ...prev, store_ids: stores.map(getStoreId).filter(Boolean) }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={800}>
            Methodes de paiement POS
          </Typography>
          <Typography color="text.secondary">
            Configurez les moyens de paiement disponibles par magasin.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadData} disabled={loading}>
          Actualiser
        </Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={800} mb={2}>
          Comptes POS
        </Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} mb={2}>
          <TextField
            label="Nom du compte"
            placeholder="Caisse magasin, CMI, Wallet..."
            value={accountForm.name}
            onChange={(event) => setAccountForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="account-type-label">Type</InputLabel>
            <Select
              labelId="account-type-label"
              label="Type"
              value={accountForm.type}
              onChange={(event) => setAccountForm((prev) => ({ ...prev, type: event.target.value }))}
            >
              <MenuItem value="cash_register">Caisse</MenuItem>
              <MenuItem value="bank">Banque</MenuItem>
              <MenuItem value="wallet">Wallet</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel id="account-store-label">Magasin</InputLabel>
            <Select
              labelId="account-store-label"
              label="Magasin"
              value={accountForm.store_id}
              onChange={(event) => setAccountForm((prev) => ({ ...prev, store_id: event.target.value }))}
            >
              <MenuItem value="">Global</MenuItem>
              {stores.map((store) => {
                const storeId = getStoreId(store);
                return <MenuItem key={storeId} value={storeId}>{getStoreLabel(store)}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <TextField
            label="Solde initial"
            type="number"
            value={accountForm.balance}
            onChange={(event) => setAccountForm((prev) => ({ ...prev, balance: event.target.value }))}
          />
          <Button variant="outlined" startIcon={<AccountBalanceWalletIcon />} onClick={handleCreateAccount} disabled={saving || loading}>
            Créer compte
          </Button>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
          {accounts.map((account) => (
            <Chip
              key={account._id}
              icon={<AccountBalanceWalletIcon />}
              label={`${getAccountLabel(account)}: ${(Number(account.balance) || 0).toFixed(2)} ${account.currency || 'MAD'}`}
              color={account.active ? 'primary' : 'default'}
              variant={account.active ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>

        <Typography variant="subtitle1" fontWeight={800} mb={1}>
          Derniers mouvements
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Compte</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sens</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell>Solde après</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Aucun mouvement de compte.</TableCell>
                </TableRow>
              ) : movements.map((movement) => (
                <TableRow key={movement._id}>
                  <TableCell>{getAccountLabel(accountById.get(String(movement.account_id)))}</TableCell>
                  <TableCell>{movement.account_type}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={movement.direction === 'in' ? 'success' : 'warning'}
                      label={movement.direction === 'in' ? 'Entrée' : 'Sortie'}
                    />
                  </TableCell>
                  <TableCell>{(Number(movement.amount) || 0).toFixed(2)} {movement.currency || 'MAD'}</TableCell>
                  <TableCell>{(Number(movement.balance_after) || 0).toFixed(2)} {movement.currency || 'MAD'}</TableCell>
                  <TableCell>{movement.source_type || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" fontWeight={800} mb={2}>
          Méthodes par magasin
        </Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <TextField
            label="Code"
            placeholder="cash, card, cmi..."
            value={formData.code}
            onChange={(event) => setFormData((prev) => ({ ...prev, code: event.target.value }))}
            helperText="Le code est normalise en minuscules avec _."
          />
          <TextField
            label="Nom"
            placeholder="Espèces, Carte bancaire..."
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl sx={{ minWidth: { xs: '100%', lg: 360 } }}>
            <InputLabel id="payment-method-stores-label">Magasins</InputLabel>
            <Select
              labelId="payment-method-stores-label"
              multiple
              value={formData.store_ids}
              onChange={(event) => setFormData((prev) => ({ ...prev, store_ids: event.target.value }))}
              input={<OutlinedInput label="Magasins" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((storeId) => (
                    <Chip key={storeId} size="small" label={storeNameById.get(storeId) || storeId} />
                  ))}
                </Box>
              )}
            >
              {stores.map((store) => {
                const storeId = getStoreId(store);
                return (
                  <MenuItem key={storeId} value={storeId}>
                    <Checkbox checked={formData.store_ids.includes(storeId)} />
                    <ListItemText primary={getStoreLabel(store)} secondary={getStoreCode(store) || storeId} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: '100%', lg: 300 } }}>
            <InputLabel id="payment-method-account-label">Compte lié</InputLabel>
            <Select
              labelId="payment-method-account-label"
              label="Compte lié"
              value={formData.account_id}
              onChange={(event) => setFormData((prev) => ({ ...prev, account_id: event.target.value }))}
            >
              <MenuItem value="">Aucun</MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>{getAccountLabel(account)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="text" onClick={selectAllStores} disabled={!stores.length}>
            Tous les magasins
          </Button>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>Actif</Typography>
            <Switch
              checked={formData.active}
              onChange={(event) => setFormData((prev) => ({ ...prev, active: event.target.checked }))}
            />
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleSubmit} disabled={saving || loading}>
            Ajouter
          </Button>
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Methode</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Compte lié</TableCell>
                <TableCell>Magasins configures</TableCell>
                <TableCell>Detail par magasin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedMethods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucune methode de paiement.
                  </TableCell>
                </TableRow>
              ) : (
                groupedMethods.map((group) => (
                  <TableRow key={group.code}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CreditCardIcon color="primary" />
                        <Typography fontWeight={800}>{group.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{group.code}</TableCell>
                    <TableCell>
                      {group.rows.some((method) => method.account_id) ? (
                        <Stack spacing={0.5}>
                          {group.rows.filter((method) => method.account_id).slice(0, 3).map((method) => (
                            <Chip
                              key={`${method._id}-account`}
                              size="small"
                              label={getAccountLabel(accountById.get(String(method.account_id)))}
                            />
                          ))}
                        </Stack>
                      ) : 'Aucun'}
                    </TableCell>
                    <TableCell>
                      {group.active}/{group.total} actifs
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {group.rows.map((method) => (
                          <Box
                            key={method._id}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              border: '1px solid',
                              borderColor: method.active ? 'success.light' : 'divider',
                              borderRadius: 999,
                              bgcolor: method.active ? 'rgba(46, 125, 50, 0.08)' : 'background.paper',
                              pl: 1.25,
                              pr: 0.5,
                              py: 0.25,
                            }}
                          >
                            <Typography variant="body2">
                              {method.store_id ? (storeNameById.get(method.store_id) || method.store_id) : 'Global'}
                            </Typography>
                            <Switch
                              size="small"
                              checked={Boolean(method.active)}
                              onChange={(event) => toggleMethod(method, event.target.checked)}
                            />
                            <Select
                              size="small"
                              value={method.account_id || ''}
                              onChange={(event) => updateMethodAccount(method, event.target.value)}
                              sx={{ minWidth: 170, '.MuiSelect-select': { py: 0.5 } }}
                            >
                              <MenuItem value="">Aucun compte</MenuItem>
                              {accounts
                                .filter((account) => !account.store_id || !method.store_id || String(account.store_id) === String(method.store_id))
                                .map((account) => (
                                  <MenuItem key={account._id} value={account._id}>{account.name || account.account_name}</MenuItem>
                                ))}
                            </Select>
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PaymentMethodsView;
